/**
 * useStatisticsWidget
 *
 * Shared state for dashboard statistics widgets backed by a single GraphQL
 * query. Owns the mechanics common to every stat widget: `lastRefresh`
 * tracking, deriving stats from the query data, the manual refresh handler,
 * auto-refresh polling, and assembling the {@link WidgetState}.
 *
 * The widget calls its own query hook (to keep its query arguments and to
 * satisfy the rules of hooks) and passes the resulting {@link StatisticsQueryResult}
 * here, along with a pure `extractStats` mapper so each widget keeps its own
 * stat shape. `extractStats` must be a stable reference (module-level function)
 * so the internal `useMemo` only recomputes when the query data changes.
 */

import { useState, useCallback, useMemo } from 'react';
import { useWidgetPolling } from './useWidgetPolling';
import type { WidgetState } from './types';

/**
 * The subset of an Apollo query result this hook consumes. Matches the shape
 * returned by the generated `useServices`/`useCapabilities`/`useTechnologies`
 * query hooks without coupling to the full Apollo type.
 */
export interface StatisticsQueryResult<TData> {
  data: TData | undefined;
  loading: boolean;
  error?: { message: string } | null;
  refetch: () => unknown;
}

export interface UseStatisticsWidgetResult<TStats> {
  /** Derived stats, or null while there is no data / the query is empty. */
  stats: TStats | null;
  /** Assembled widget state for {@link DashboardWidget}. */
  widgetState: WidgetState<TStats>;
  /** Refetch + stamp `lastRefresh`; pass to the widget's `onRefresh`. */
  handleRefresh: () => void;
}

export function useStatisticsWidget<TData, TStats>(
  query: StatisticsQueryResult<TData>,
  extractStats: (data: TData | undefined) => TStats | null,
  refreshInterval: number
): UseStatisticsWidgetResult<TStats> {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const { data, loading, error, refetch } = query;

  const stats = useMemo(() => extractStats(data), [data, extractStats]);

  const widgetState: WidgetState<TStats> = {
    data: stats,
    loading,
    error: error?.message || null,
    lastRefresh,
  };

  const handleRefresh = useCallback(() => {
    refetch();
    setLastRefresh(new Date());
  }, [refetch]);

  useWidgetPolling({
    interval: refreshInterval,
    onRefresh: handleRefresh,
    enabled: !loading && !error,
  });

  if (data && !lastRefresh) {
    setLastRefresh(new Date());
  }

  return { stats, widgetState, handleRefresh };
}
